/**
 * Catches near-miss email domains — `gmial.com`, `yaho.com`, `gmail.co` — so a
 * form can offer "did you mean …?" before someone waits on a link that will
 * never arrive. Suggestion only: plenty of real addresses sit one character
 * from a common provider, so callers must never block on it.
 *
 * Mirror of `apps/web/lib/email-typos.ts` (the canonical copy) — the
 * two apps have separate builds and cannot import each other. Change both
 * together.
 */

/** Providers common enough that a near-miss is almost certainly a typo. */
const COMMON_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.ca',
  'ymail.com',
  'rocketmail.com',
  'hotmail.com',
  'hotmail.co.uk',
  'outlook.com',
  'outlook.co.uk',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'zoho.com',
  'gmx.com',
  'mail.com',
  'yandex.com',
  'comcast.net',
  'verizon.net',
  'att.net',
  'sbcglobal.net',
  'bellsouth.net',
  'cox.net',
  'charter.net',
  'earthlink.net',
  'shaw.ca',
  'sympatico.ca',
  'rogers.com',
  'telus.net',
  'btinternet.com',
];

/** Endings worth fixing on any domain, not just the common providers. */
const COMMON_TLDS = ['com', 'net', 'org', 'edu', 'gov', 'io', 'co', 'ca', 'us', 'co.uk'];

/** Edit distance that counts a swapped pair of letters (`cmo` for `com`) as
 *  one mistake, since that is what fast typing actually produces. */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d: number[][] = Array.from({ length: rows }, (_, i) =>
    Array.from({ length: cols }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

/** Closest entry within `maxDistance`, or undefined when nothing is close. */
function closest(value: string, candidates: string[], maxDistance: number): string | undefined {
  let best: string | undefined;
  let bestDistance = maxDistance + 1;
  for (const candidate of candidates) {
    const distance = editDistance(value, candidate);
    if (distance > 0 && distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * A corrected address to offer the user, or undefined when the address looks
 * fine (or is too far from anything known to guess at).
 */
export function suggestEmailCorrection(email: string | undefined): string | undefined {
  const trimmed = email?.trim().toLowerCase();
  if (!trimmed) return undefined;
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1) return undefined;

  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  if (COMMON_DOMAINS.includes(domain)) return undefined;

  // One transposed or dropped letter in a well-known provider. Short domains
  // get a tighter budget so `me.com` doesn't swallow unrelated addresses.
  const domainGuess = closest(domain, COMMON_DOMAINS, domain.length <= 8 ? 1 : 2);
  if (domainGuess) return `${local}@${domainGuess}`;

  // Otherwise the ending alone might be off (`proxi.cmo`, `proxi.con`).
  const firstDot = domain.indexOf('.');
  if (firstDot <= 0) return undefined;
  const host = domain.slice(0, firstDot);
  const tld = domain.slice(firstDot + 1);
  if (COMMON_TLDS.includes(tld)) return undefined;
  const tldGuess = closest(tld, COMMON_TLDS, 1);
  return tldGuess ? `${local}@${host}.${tldGuess}` : undefined;
}
